import React from 'react';
import { format, parseISO } from 'date-fns';
import { UserIcon, CheckCircleIcon, PencilIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { MemberAvailabilityBlock } from '../../types/availability';

interface AvailabilityBlockProps {
  block: MemberAvailabilityBlock;
  onEdit: () => void;
}

const PX_PER_MINUTE = 24 / 30; // 24px per 30-min slot = 0.8px per minute
const START_HOUR = 0; // Grid starts at midnight (00:00)

const AvailabilityBlock: React.FC<AvailabilityBlockProps> = ({ block, onEdit }) => {
  const startTime = format(parseISO(`2000-01-01T${block.start_time}`), 'h:mm a');
  const endTime = format(parseISO(`2000-01-01T${block.end_time}`), 'h:mm a');

  const [startHour, startMin] = block.start_time.split(':').map(Number);
  const [endHour, endMin] = block.end_time.split(':').map(Number);

  // Minute-based positioning (supports any increment)
  const startMinutes = (startHour - START_HOUR) * 60 + startMin;
  const endMinutes = (endHour - START_HOUR) * 60 + endMin;

  const topPosition = startMinutes * PX_PER_MINUTE;
  const height = Math.max((endMinutes - startMinutes) * PX_PER_MINUTE, 12); // min 12px

  const isBreak = block.block_type === 'break';

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isBreak) return;  // breaks are display-only here; manage via "Add break"
    onEdit();
  };

  return (
    <div
      className={`absolute left-1 right-1 flex items-center justify-between px-2 rounded text-xs font-medium transition-all duration-200 shadow-sm group pointer-events-auto ${
        isBreak
          ? 'bg-clinic-dark/15 text-clinic-dark border border-clinic-dark/40 border-dashed cursor-default'
          : !block.is_booked
          ? 'cursor-pointer hover:shadow-md bg-clinic-primary/20 text-clinic-primary border border-clinic-primary/30 hover:bg-clinic-primary/30 hover:scale-105'
          : block.booking_type === 'pod'
          ? 'cursor-pointer hover:shadow-md bg-clinic-brown/20 text-clinic-brown border border-clinic-brown/30 border-dashed hover:bg-clinic-brown/30 hover:scale-105'
          : block.booking_type === 'self'
          ? 'cursor-pointer hover:shadow-md bg-clinic-gold/20 text-clinic-gold border border-clinic-gold/30 border-dashed hover:bg-clinic-gold/30 hover:scale-105'
          : 'cursor-pointer hover:shadow-md bg-clinic-accent/20 text-clinic-accent border border-clinic-accent/30 hover:bg-clinic-accent/30 hover:scale-105'
      }`}
      style={{
        top: `${topPosition}px`,
        height: `${height}px`,
      }}
      onClick={handleClick}
      title={isBreak
        ? `Break${block.break_reason ? `: ${block.break_reason}` : ''} (${startTime} - ${endTime})`
        : `${startTime} - ${endTime} • Click to edit or delete this availability`}
      data-testid={`availability-block-${block.id}`}
      data-guide={!isBreak && !block.is_booked ? 'availability-block' : undefined}
    >
      <span className="truncate font-semibold">
        {isBreak ? 'Break' : `${startTime} - ${endTime}`}
      </span>
      <div className="flex items-center space-x-1 ml-2">
        {isBreak ? (
          <NoSymbolIcon className="h-3 w-3 flex-shrink-0" />
        ) : block.is_booked ? (
          <UserIcon className="h-3 w-3 flex-shrink-0" />
        ) : (
          <CheckCircleIcon className="h-3 w-3 flex-shrink-0" />
        )}
        {!isBreak && <PencilIcon className="h-3 w-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />}
      </div>
    </div>
  );
};

export default React.memo(AvailabilityBlock);